import useDriftClient from '../../useDriftClient';
import invariant from 'tiny-invariant';
import { BigNum, PublicKey, ReferrerInfo } from '@velocity-exchange/sdk';
import { useCallback } from 'react';
import useCurrentAuthority from '../../useCurrentAuthority';
import { useSpotMarketsLookup } from '../../spotMarkets/useSpotMarketsLookup';
import {
	createUserAndDepositCollateralBaseIxs,
	MarketId,
	convertLeverageToMarginRatio,
} from '@velocity-exchange/common';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useGetTxParams } from '../transactions/useGetTxParams';
import { notify } from 'src/utils/notifications';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import {
	DEFAULT_ACCOUNT_NAMES_BY_POOL_ID,
	MAX_COMPUTE_UNITS,
} from 'src/constants/constants';
import { useTxErrorHandler } from '../transactions/useTxErrorHandler';
import { sleep } from 'src/utils/dateTime';
import { getReferrerInfo } from 'src/utils/referral';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';
import captureException from 'src/utils/captureException';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { useAccountInitialization } from './useAccountInitialization';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignDriftActionWithFallback } from 'src/hooks/wallets/useSponsoredSignDriftActionWithFallback';
import { TransactionInstruction } from '@solana/web3.js';

interface CreateNewSubaccountProps {
	depositAmount: BigNum;
	spotMarketIndex: number;
	poolId?: number;
	accountName?: string;
	/** Takes the deposit from this subaccount */
	fromSubAccountId?: number;
	maxLeverage?: number | undefined;
}

export const useCreateNewSubaccount = () => {
	const driftClient = useDriftClient();
	const authority = useCurrentAuthority();
	const spotMarketConfigsLookup = useSpotMarketsLookup();
	const userStatsAccount = useDriftStore((s) => s.userStatsAccount);
	const isGeoBlocked = useDriftStore((s) => s.isGeoblocked);
	const countryCode = useDriftStore((s) => s.countryCode);
	const referrerName = useDriftStore((s) => s.referrerParam);
	const { embeddedWallet } = usePrivyWalletContext();

	// Store setters and getters
	const setDriftAccountsStore = useDriftAccountsStore((s) => s.set);
	const getAccountsState = useDriftAccountsStore((s) => s.get);
	const walletAdapter = useDriftStore((s) => s.wallet.current.adapter);

	const getTxParams = useGetTxParams();
	const handleTxError = useTxErrorHandler();
	const {
		runAccountInitializationTxAndHandling,
		handleCreateDriftUserSuccess,
	} = useAccountInitialization();

	const createSubaccountAction = useCallback(
		async (
			wallet: UiEnhancedIWalletV2,
			ixs: TransactionInstruction[],
			spotMarketIndex: number
		) => {
			const marketId = MarketId.createSpotMarket(spotMarketIndex);
			const txParams = getTxParams({
				marketId,
				computeUnits: MAX_COMPUTE_UNITS,
				useSimulatedComputeUnits: false,
			});
			const txSig = await wallet.signAndSendTransaction(ixs, txParams);
			return txSig;
		},
		[getTxParams]
	);

	const wrappedCreateSubaccount = useSponsoredSignDriftActionWithFallback(
		createSubaccountAction
	);

	const createNewSubaccount = useCallback(
		async (props: CreateNewSubaccountProps): Promise<boolean> => {
			const {
				depositAmount,
				spotMarketIndex,
				poolId,
				accountName,
				fromSubAccountId,
				maxLeverage,
			} = props;

			const spotMarketConfig = spotMarketConfigsLookup[spotMarketIndex];

			invariant(driftClient, 'Drift client not found');
			invariant(authority, 'Authority not found');
			invariant(spotMarketConfig, 'Spot market config not found');

			if (isGeoBlocked && countryCode !== 'US') {
				notify({
					type: 'error',
					message: 'Geoblocked',
					description:
						'You are not allowed to use Drift from a restricted territory',
					lengthMs: 5000,
				});
				return false;
			}
			if (!walletAdapter || !walletAdapter.publicKey) return false;

			const accountsState = getAccountsState();
			const hasAccountForPool = Object.values(accountsState.accounts).some(
				(account) => account?.client?.getUserAccountOrThrow().poolId === poolId
			);

			const subAccountIdToInit =
				userStatsAccount?.numberOfSubAccountsCreated ?? 0;
			const newAccountName = accountName
				? accountName
				: !hasAccountForPool && !!poolId
					? DEFAULT_ACCOUNT_NAMES_BY_POOL_ID[poolId]
					: undefined;
			const customMaxMarginRatio = convertLeverageToMarginRatio(
				maxLeverage ?? 0
			);
			const referrerInfo: ReferrerInfo | undefined = await getReferrerInfo(
				driftClient,
				referrerName ?? ''
			);
			const isReferred = !!referrerInfo;

			const toastId = `create_new_subaccount_${Date.now()}`;
			notify({
				id: toastId,
				type: 'awaiting_wallet_confirmation',
				message: 'Creating new subaccount.',
				showUntilCancelled: true,
			});

			let initializationTxSig: string | undefined;

			const executeTransaction = async () => {
				try {
					const { ixs } = await createUserAndDepositCollateralBaseIxs({
						velocityClient: driftClient,
						amount: depositAmount.val,
						spotMarketConfig,
						authority,
						userStatsAccount,
						referrerName: referrerName ?? undefined,
						accountName: newAccountName,
						poolId,
						fromSubAccountId,
						customMaxMarginRatio,
						delegate: embeddedWallet
							? new PublicKey(embeddedWallet.address)
							: undefined,
					});

					const txSig = await wrappedCreateSubaccount(ixs, spotMarketIndex);

					return { txSig };
				} catch (error: unknown) {
					handleTxError(error, toastId);
					return undefined;
				}
			};

			const customSuccessHandler = async (
				userIdToInit: number,
				toastId?: string,
				switchAccount?: boolean
			) => {
				await handleCreateDriftUserSuccess({
					userIdToInit,
					toastId,
					switchAccount,
					customMessages: {
						successMessage: 'New subaccount created.',
					},
				});

				await sleep(3000);

				if (initializationTxSig) {
					setDriftAccountsStore((s) => {
						s.userAccountNotInitialized = false;
					});
				}
			};

			const result = await runAccountInitializationTxAndHandling({
				isReferred,
				driftClient,
				userIdToInit: subAccountIdToInit,
				toastId,
				runInitializationTransaction: async () => {
					try {
						const txResult = await executeTransaction();
						if (!txResult) return undefined;

						initializationTxSig = txResult.txSig;
						return { txSig: txResult.txSig };
					} catch (e: any) {
						captureException(e);
						TransactionErrorHandler.handleError({
							error: e,
							toastId,
							wallet: walletAdapter,
						});
						return undefined;
					}
				},
				poolId,
				customMessages: {
					successMessage: 'New subaccount created.',
					initializingMessage: 'Deposit successful',
				},
				onSuccess: customSuccessHandler,
			});

			return result;
		},
		[
			driftClient,
			authority,
			spotMarketConfigsLookup,
			userStatsAccount,
			isGeoBlocked,
			countryCode,
			referrerName,
			embeddedWallet,
			walletAdapter,
			getAccountsState,
			handleTxError,
			setDriftAccountsStore,
			handleCreateDriftUserSuccess,
			runAccountInitializationTxAndHandling,
			wrappedCreateSubaccount,
		]
	);

	return createNewSubaccount;
};
